/* eslint-disable react/jsx-no-literals */

import SiteHeader from 'components/header/SiteHeader'
import Thirdparty from 'components/service/Thirdparty'
import { teamRoleBadge } from 'components/community/teamRoleBadge'
import { publicConfig } from 'config'
import { getds } from 'ds'
import dynamic from 'next/dynamic'
import { useCallback, useEffect } from 'react'
import ContentBackground from 'shared-components/ad/ContentBackground'
import { CommentHubAPIContext } from 'shared-components/contexts/CommentHubAPIContext'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { ConversationContext } from 'shared-components/comments/conversation/ConversationContext'
import { PollStoreContext, PollStoreProps } from 'shared-components/polls/PollStoreContext'
import { DealBarContext } from 'shared-components/contexts/DealBarContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { PlaywireContext } from 'shared-components/contexts/PlaywireContext'
import { SearchContext } from 'shared-components/contexts/SearchContext'
import { ThemeContext } from 'shared-components/contexts/ThemeContext'
import { UIVariantContext } from 'shared-components/contexts/UIVariantContext'
import { defaultAdInferePath } from 'shared-components/hooks/use-ad-path'
import { useIsPreview } from 'shared-components/hooks/use-is-preview'
import { useGlobalCdpClient } from 'shared-components/hooks/use-global-cdp-client'
import Footer from 'shared-components/layout/Footer'
import Main from 'shared-components/layout/Main'
import PageTransition from 'shared-components/layout/PageTransition'
import PopupWrapper from 'shared-components/layout/PopupWrapper'
import UnderNavBlock from 'shared-components/layout/UnderNavBlock'
import AdPath from 'shared-components/service/AdPath'
import styleUtils from 'shared-components/styles/util.module.css'
import { DSLayoutProps } from 'shared-definitions/types'
import Logo from 'shared-svg/AuthorityMedia.svg'
import { DSGetDealBar } from 'types.sg.ds'
import styles from './Layout.module.css'
import { useClearbuyCookies } from 'shared-components/hooks/use-clearbuy-cookies'

const AdminPanel = dynamic(() => import('shared-components/layout/AdminPanel'), { ssr: false })
const NotificationsLayer = dynamic(() => import('shared-components/layout/NotificationsLayer'))
// Sign-in surface. ssr: false because it measures its container to size the
// Google-owned button iframe, which only exists in the browser.
const LoginDialog = dynamic(() => import('shared-components/ui/LoginDialog'), { ssr: false })

// The one poll store's WordPress calls (polls in the conversation): the in-article poll and the
// conversation panel's read and write the same store, mounted around the page below.
/**
 * The under-nav table of contents shows from lg only (Guillaume, 2026-10-25): below lg the top of
 * the screen is the conversation bar's, and the strip on top of it read as clutter. A page's own
 * `tocVisibility` still wins.
 */
const TOC_VISIBILITY: DSLayoutProps['tocVisibility'] = { default: false, breakpoints: { lg: true } }

const pollStoreProps: PollStoreProps = {
  sendPoll: async feed => (await getds()).sendPoll(feed),
  getPoll: async pollId => (await getds()).getPoll(pollId),
}

const Layout: React.FC<React.PropsWithChildren & { layoutProps: DSLayoutProps }> = ({
  children,
  layoutProps,
}) => {
  const isPreview = useIsPreview()
  const { queue } = NotificationsContext.useContainer()
  const { ready, theme } = ThemeContext.useContainer()
  const { showAdminPanel } = CommonDataContext.useContainer()

  useGlobalCdpClient({
    eventsCollectorUrl: publicConfig('cdpEventsCollectorUrl'),
  })
  useClearbuyCookies({
    clearbuyCookieUrl: publicConfig('clearbuyCookieUrl'),
  })

  useEffect(() => {
    if (!ready) {
      return
    }

    document.body.classList[theme === 'dark' ? 'add' : 'remove'](styleUtils.darkTheme)
    document.body.dataset.currentTheme = theme
  }, [ready, theme])

  const getDealbar = useCallback(async (...args: Parameters<DSGetDealBar>) => {
    const dataSource = await getds()
    return dataSource.getDealBar(...args)
  }, [])
  return (
    <PlaywireContext.Provider
      initialState={{
        inst: null,
        enabled: !publicConfig('adsDisabled'),
        adClassList: publicConfig('adClassList'),
      }}
    >
      <SearchContext.Provider
        initialState={{
          productsSuggestions: async (...args) => {
            const dataSource = await getds()
            return dataSource.getProductsSuggestions(...args)
          },
          search: async (...args) => {
            const dataSource = await getds()
            return dataSource.getSearchPage(...args)
          },
        }}
      >
        <CommentHubAPIContext.Provider
          initialState={{
            apiUrl: publicConfig('commentHubApiUrl'),
            site: publicConfig('commentHubSite'),
            appUrl: publicConfig('appUrl'),
            // commentHub's role is THE source of the SOUNDGUYS TEAM pill (2026-10-23): a team
            // role wears it on every comment surface; the raw role word is never shown.
            roleBadge: teamRoleBadge,
            // Emoji reactions under article and best-list comments (2026-10-28) in place of the
            // thumbs; discussion answers keep "Helpful" through their frame's voteStyle.
            reactions: true,
          }}
        >
          <ConversationContext.Provider>
            <UIVariantContext.Provider
              initialState={{
                colors: 'soundguys',
                postPreviewScore: 'ring',
                buttonStyle: 'outline',
              }}
            >
              <DealBarContext.Provider initialState={{ getDealbar, dealbar: null }}>
                <AdPath
                  inferePath={(path, tags) => {
                    if (path === '/') {
                      return '/'
                    }
                    return defaultAdInferePath(path, tags)
                  }}
                />
                {showAdminPanel ? <AdminPanel editPostTpl={publicConfig('postEditLink')} /> : null}
                <SiteHeader />
                <UnderNavBlock
                  tocVisibility={layoutProps?.tocVisibility ?? TOC_VISIBILITY}
                  hideDealBar={layoutProps?.hideDealBar}
                  dealBarVariant={layoutProps?.dealBarVariant}
                />
                <ContentBackground />
                <PollStoreContext.Provider initialState={pollStoreProps}>
                  <Main className={styles.main}>{children}</Main>
                </PollStoreContext.Provider>
                <Footer logo={Logo} logoLink={{ href: publicConfig('authorityMediaLink') }} />
                <PopupWrapper />
                <LoginDialog />
                <PageTransition />
                {queue.length ? <NotificationsLayer /> : null}
                {!isPreview ? <Thirdparty /> : null}
              </DealBarContext.Provider>
            </UIVariantContext.Provider>
          </ConversationContext.Provider>
        </CommentHubAPIContext.Provider>
      </SearchContext.Provider>
    </PlaywireContext.Provider>
  )
}

export default Layout
